import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import ConfirmationPopup from "../../../shared/ConfirmationPopup/ConfirmationPopup";
import OpcHeader from "../_shared/OpcHeader/OpcHeader";
import OpcSplitPanels from "../_shared/OpcSplitPanels/OpcSplitPanels";
import OpcChatPanel from "../_shared/OpcChatPanel/OpcChatPanel";
import { OpcPageError } from "../_shared/OpcPageState/OpcPageState";
import { OpcChatSkeleton } from "../_shared/OpcChatView/OpcChatView";
import { OpcPanelSkeleton } from "../_shared/OpcSkeleton/OpcSkeleton";
import okrPlansCoachStore, {
  useOkrPlansCoachStore,
} from "../_store/okrPlansCoachStore";
import okrPlanCoachChatStore, {
  COACH_CARD,
  COACH_CHAT_EMIT,
} from "../_socket/okrPlanCoachChatStore";
import useCoachPlan from "../_hooks/useCoachPlan";
import { COACH_FEATURE_SLUG } from "../_config/okrPlansCoachConfig";
import { firstNameOf } from "../_config/okrPlansCoachFormat";
import { notifyError } from "../_config/okrPlansCoachNotify";
import {
  companyPath,
  dashboardPath,
  planPath,
} from "../_config/okrPlansCoachRoutes";
import ReviewPanel from "./ReviewPanel/ReviewPanel";

// ═══════════════════════════════════════════════════════════════════════════
// Final review (coach) — BE 2.9; CD20, CD23. Chat with the review agent on
// the left (coach-mode `/new-chat`, `okr_plan_coach_final`): summary changes
// and the remark come as cards. The panel on the right has Finalise review
// (ConfirmationPopup → `POST …/final-review/finalise`). The chat is started
// with `POST …/final-review/start` once the owner has submitted; after
// Finalise it stays as a record (read-only).
// ═══════════════════════════════════════════════════════════════════════════

const selectLastCompany = (s) => s.lastCompany;

const OkrPlansCoachReview = () => {
  const { planUniqueId: planId } = useParams();
  const history = useHistory();
  const { plan } = useCoachPlan(planId);
  const lastCompany = useOkrPlansCoachStore(selectLastCompany);
  const selectReview = useCallback((s) => s.reviews[planId], [planId]);
  const review = useOkrPlansCoachStore(selectReview);
  const selectFinalising = useCallback(
    (s) => !!s.pending[`finalise:${planId}`],
    [planId],
  );
  const finalising = useOkrPlansCoachStore(selectFinalising);
  const [error, setError] = useState(null);
  const [confirm, setConfirm] = useState(false);

  const load = useCallback(() => {
    setError(null);
    okrPlansCoachStore
      .getState()
      .fetchFinalReview(planId)
      .catch((err) => setError(err));
  }, [planId]);
  useEffect(load, [load]);

  const finalised = review?.state === "REVIEWED" || !!review?.finalised_at;
  // Submitted, no coach chat yet → start it (idempotent).
  useEffect(() => {
    if (
      !review ||
      finalised ||
      !review.submitted ||
      review.coach_chat_unique_id
    )
      return;
    okrPlansCoachStore
      .getState()
      .startFinalReview(planId)
      .catch((err) => setError(err));
  }, [review, finalised, planId]);

  const owner = firstNameOf(plan?.owner?.name) || "The owner";
  const toPlan = () => history.push(planPath(planId));
  const crumbs = [
    { title: "Dashboard", go: () => history.push(dashboardPath()) },
    ...(lastCompany
      ? [
          {
            title: lastCompany.name,
            go: () =>
              history.push(
                companyPath(lastCompany.id, lastCompany.quarterCompanyId),
              ),
          },
        ]
      : []),
    { title: plan?.objective?.text || "OKR plan", go: toPlan },
    { title: "Final review" },
  ];

  const chatId = review?.coach_chat_unique_id;
  const onCard = (kind, card) => {
    const chat = okrPlanCoachChatStore.getState();
    const act = (event) => () =>
      chat.cardAction(chatId, card.message_id, event);
    return kind === COACH_CARD.REMARK
      ? {
          onConfirm: act(COACH_CHAT_EMIT.REMARK_SAVE),
          onDecline: act(COACH_CHAT_EMIT.REMARK_KEEP_EDITING),
        }
      : {
          onConfirm: act(COACH_CHAT_EMIT.FINAL_CHANGE_CONFIRM),
          onDecline: act(COACH_CHAT_EMIT.FINAL_CHANGE_CANCEL),
        };
  };

  const finalise = () => {
    setConfirm(false);
    okrPlansCoachStore.getState().finalise(planId).catch(notifyError);
  };

  let content;
  if (error)
    content = (
      <OpcPageError
        error={error}
        onRetry={load}
        onBack={toPlan}
        backLabel="Plan"
      />
    );
  else if (!review || !plan)
    // CD50: the real split layout with the chat skeleton from the start —
    // one skeleton until the messages are in.
    content = (
      <OpcSplitPanels
        id="project-review"
        left={<OpcChatSkeleton />}
        right={<OpcPanelSkeleton />}
      />
    );
  else {
    // The coach chat is being started (submitted, no chat yet) → still the
    // chat skeleton (CD50).
    const starting = !chatId && !!review.submitted && !finalised;
    const left = starting ? (
      <OpcChatSkeleton />
    ) : chatId ? (
      <OpcChatPanel
        chatId={chatId}
        featureSlug={COACH_FEATURE_SLUG.FINAL}
        owner={{ planId }}
        readOnly={finalised}
        starters={[
          "Draft my remark",
          "Tighten the executive summary",
          `What should ${owner} carry into next quarter?`,
        ]}
        onCard={onCard}
        footerNote={
          finalised ? (
            <span className="small light-text">
              Review finalised — this conversation is kept for your records.
            </span>
          ) : null
        }
      />
    ) : (
      <div className="d-flex align-items-center justify-content-center h-100 px-4 text-center">
        <span className="light-text small">
          The review chat opens once {owner} submits the review.
        </span>
      </div>
    );
    const right = (
      <ReviewPanel
        plan={plan}
        review={review}
        owner={owner}
        ownerFull={plan.owner?.name}
        finalising={finalising}
        onFinalise={() => setConfirm(true)}
      />
    );
    content = <OpcSplitPanels id="project-review" left={left} right={right} />;
  }

  return (
    <div className="opc-screen">
      <OpcHeader crumbs={crumbs} />
      {content}
      <ConfirmationPopup
        isOpen={confirm}
        title={`Finalise ${owner}'s review?`}
        content={`${owner} sees the review summary and your remarks straight away, and the plan is marked Reviewed.`}
        confirm="Finalise review"
        cancel="Not yet"
        onConfirm={finalise}
        onCancel={() => setConfirm(false)}
      />
    </div>
  );
};

export default OkrPlansCoachReview;
